import { useEffect } from "react";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * SmoothScroll wires the marketing site's native document scroll through
 * Lenis, then gives GSAP/ScrollTrigger the same animation clock. Keeping one
 * requestAnimationFrame source prevents scroll-linked effects from drifting
 * behind the smoothed scroll position.
 *
 * This is intentionally a renderless, client-only component: useEffect never
 * runs during SSR, and Lenis continues to use the normal <html> scroller.
 */
export function SmoothScroll() {
  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let stop = () => {};

    const start = () => {
      // Respect the platform preference by leaving native scrolling untouched.
      if (reducedMotion.matches) return () => {};

      gsap.registerPlugin(ScrollTrigger);
      const lenis = new Lenis({
        autoRaf: false,
        anchors: true,
      });
      const onScroll = () => ScrollTrigger.update();
      const tick = (time: number) => lenis.raf(time * 1000);
      const handOffToIframe = (event: PointerEvent) => {
        if (
          event.target instanceof HTMLIFrameElement &&
          event.target.hasAttribute("data-lenis-prevent") &&
          lenis.isScrolling === "smooth"
        ) {
          // The frame receives its own wheel events. Finish the parent animation
          // here so the board doesn't keep moving underneath the user's cursor.
          lenis.scrollTo(lenis.actualScroll, { immediate: true, force: true });
        }
      };

      lenis.on("scroll", onScroll);
      document.addEventListener("pointerover", handOffToIframe);
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(0);
      ScrollTrigger.refresh();

      return () => {
        document.removeEventListener("pointerover", handOffToIframe);
        lenis.off("scroll", onScroll);
        gsap.ticker.remove(tick);
        lenis.destroy();
        // Restore GSAP's default behaviour for dev HMR and any non-marketing
        // React surface that mounts after this layout unmounts.
        gsap.ticker.lagSmoothing(500, 33);
      };
    };

    const restart = () => {
      stop();
      stop = start();
    };

    restart();
    reducedMotion.addEventListener("change", restart);
    return () => {
      reducedMotion.removeEventListener("change", restart);
      stop();
    };
  }, []);

  return null;
}
